
import React, { useState, useEffect, useRef } from 'react';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
}

const LazyImage: React.FC<LazyImageProps> = ({ src, alt, className = "" }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px', // Increased margin to load earlier
        threshold: 0.01 // More sensitive threshold
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
      observer.disconnect();
    };
  }, []);

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  return (
    <div 
      ref={imageRef}
      className={`relative overflow-hidden ${className}`}
    >
      {isInView && (
        <>
          <div className={`${isLoaded ? 'opacity-0' : 'opacity-100'} absolute inset-0 bg-gray-100 animate-pulse transition-opacity duration-500`}></div>
          <div className={`reveal-image ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500`}>
            <img
              src={src}
              alt={alt}
              onLoad={handleImageLoad}
              className="w-full h-full object-cover"
            />
          </div>
        </>
      )}
      {!isInView && (
        <div className="w-full h-full bg-gray-100 animate-pulse"></div>
      )}
    </div>
  );
};

export default LazyImage;
